/**
 * Settings > Status icons — assign Lucide icons to campaigns and teams (Status page left menu).
 * Tabbed (Campaigns / Teams), compact layout, icon reference grid, auto-save on change.
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { DEFAULT_CAMPAIGNS, DEFAULT_TEAMS, STATUS_ICON_OPTIONS, getStatusIcon } from '../../lib/statusIcons';

export interface StatusIconsConfig {
  campaigns?: Record<string, string>;
  teams?: Record<string, string>;
}

type Tab = 'campaigns' | 'teams';

export default function SettingsStatusIcons() {
  const { getToken } = useAuth();
  const [campaignIcons, setCampaignIcons] = useState<Record<string, string>>({});
  const [teamIcons, setTeamIcons] = useState<Record<string, string>>({});
  const [tab, setTab] = useState<Tab>('campaigns');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      try {
        const base = typeof window !== 'undefined' ? window.location.origin : '';
        const res = await fetch(`${base}/api/config?keys=status_icons`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const cfg = (data.status_icons || {}) as StatusIconsConfig;
          setCampaignIcons(cfg.campaigns || {});
          setTeamIcons(cfg.teams || {});
        }
      } catch {
        // keep defaults
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getToken]);

  const save = useCallback(async (campaigns: Record<string, string>, teams: Record<string, string>) => {
    const token = await getToken();
    if (!token) return;
    setSaving(true);
    setError(null);
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    try {
      const res = await fetch(`${base}/api/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          status_icons: { campaigns, teams },
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [getToken]);

  const handleCampaignChange = (name: string, value: string) => {
    const next = { ...campaignIcons, [name]: value };
    setCampaignIcons(next);
    save(next, teamIcons);
  };

  const handleTeamChange = (name: string, value: string) => {
    const next = { ...teamIcons, [name]: value };
    setTeamIcons(next);
    save(campaignIcons, next);
  };

  if (loading) return <p style={{ color: 'var(--text-muted)' }}>Loading…</p>;

  const tabStyle = (active: boolean) => ({
    padding: '0.5rem 1rem',
    border: 'none',
    borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
    background: 'transparent',
    color: active ? 'var(--accent)' : 'var(--text-muted)',
    cursor: 'pointer',
    fontSize: '0.9375rem',
    fontWeight: active ? 600 : 400,
  });

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Status icons</h2>
      <p style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>
        Set the icon shown in the Status page left menu for each campaign and team. Icons from{' '}
        <a href="https://lucide.dev/icons/" target="_blank" rel="noreferrer">Lucide</a>. Changes save automatically.
      </p>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <button type="button" onClick={() => setTab('campaigns')} style={tabStyle(tab === 'campaigns')}>
          Campaigns
        </button>
        <button type="button" onClick={() => setTab('teams')} style={tabStyle(tab === 'teams')}>
          Teams
        </button>
      </div>

      {tab === 'campaigns' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          {DEFAULT_CAMPAIGNS.map((name) => {
            const Icon = getStatusIcon(campaignIcons[name] || 'LayoutList');
            return (
              <div
                key={name}
                style={{
                  background: 'var(--widget-bg)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '8px',
                  padding: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.75rem',
                }}
              >
                <Icon size={28} strokeWidth={2} style={{ flexShrink: 0, color: 'var(--accent)' }} />
                <span style={{ fontSize: '0.9375rem', fontWeight: 600 }}>{name}</span>
                <select
                  value={campaignIcons[name] || 'LayoutList'}
                  onChange={(e) => handleCampaignChange(name, e.target.value)}
                  style={{ width: '100%', padding: '0.5rem 0.5rem', borderRadius: '6px', border: '1px solid var(--header-border)', background: 'var(--bg)', color: 'var(--text)', fontSize: '0.8125rem' }}
                >
                  {STATUS_ICON_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      )}

      {tab === 'teams' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          {DEFAULT_TEAMS.map((name) => {
            const Icon = getStatusIcon(teamIcons[name] || 'LayoutList');
            return (
              <div
                key={name}
                style={{
                  background: 'var(--widget-bg)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '8px',
                  padding: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.75rem',
                }}
              >
                <Icon size={28} strokeWidth={2} style={{ flexShrink: 0, color: 'var(--accent)' }} />
                <span style={{ fontSize: '0.9375rem', fontWeight: 600 }}>{name}</span>
                <select
                  value={teamIcons[name] || 'LayoutList'}
                  onChange={(e) => handleTeamChange(name, e.target.value)}
                  style={{ width: '100%', padding: '0.5rem 0.5rem', borderRadius: '6px', border: '1px solid var(--header-border)', background: 'var(--bg)', color: 'var(--text)', fontSize: '0.8125rem' }}
                >
                  {STATUS_ICON_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      )}

      <h3 style={{ marginTop: '1.5rem', marginBottom: '0.5rem', fontSize: '0.9375rem' }}>Icon reference</h3>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginBottom: '0.75rem' }}>
        All available icons and their names (same as in the dropdowns above).
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.5rem' }}>
        {STATUS_ICON_OPTIONS.map((opt) => {
          const Icon = getStatusIcon(opt.value);
          return (
            <div
              key={opt.value}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.4rem 0.6rem',
                background: 'var(--widget-bg)',
                borderRadius: '6px',
                fontSize: '0.8125rem',
              }}
            >
              <Icon size={20} strokeWidth={2} style={{ flexShrink: 0 }} />
              <span>{opt.label}</span>
            </div>
          );
        })}
      </div>

      {saving && <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginTop: '0.75rem' }}>Saving…</p>}
      {error && <p style={{ color: 'var(--error)', fontSize: '0.875rem', marginTop: '0.5rem' }}>{error}</p>}
    </div>
  );
}
